import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { selectTagsStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Modal, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateCalcBlackList, useUpdateCalcBlackList } from "../../../../../common/api/queries/calcBlackList";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import InstitutionSelect from "../../../../enumerations/components/form/InstitutionSelect";
import { InstitutionType } from "../../../../institution/enums";
import { CalcType, calcTypeTMap } from "../../../enums";
import { CalcTypeTag } from "../../../records/components/CalcTypeTag";
import { CalcBlacklist, CreateUpdateCalcBlacklist } from "../../types";

interface Props {
  calcBlacklist?: CalcBlacklist;
  open: boolean;
  onFormCancel: () => void;
}

export const CalcBlacklistForm = ({ calcBlacklist, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateCalcBlacklist>();

  const createCalcBlackList = useCreateCalcBlackList();
  const updateCalcBlackList = useUpdateCalcBlackList();

  useEffect(() => {
    if (open && calcBlacklist) {
      form.setFieldsValue({
        optimisticLockVersion: calcBlacklist.optimisticLockVersion,
        calcType: calcBlacklist.calcType,
        agentId: calcBlacklist.agent.id,
        institutionId: calcBlacklist.institution.id,
      });
    }
  }, [calcBlacklist, open, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (calcBlacklist) {
          updateCalcBlackList.mutate(
            {
              id: calcBlacklist.id,
              object: values,
            },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "calc.blacklist.attrs", error.violations),
            },
          );
        } else {
          createCalcBlackList.mutate(values, {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "calc.blacklist.attrs", error.violations),
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={
        calcBlacklist ? t("calc.blacklist.titles.updateCalcBlacklist") : t("calc.blacklist.titles.createCalcBlacklist")
      }
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createCalcBlackList.isPending || updateCalcBlackList.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={calcBlacklist} />

      <Form form={form} layout="vertical" name="calcBlacklistForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item name="calcType" label={t("calc.enums.calcType._label")} rules={[validations.notNull]}>
              <Select
                {...selectTagsStandardProps(calcTypeTMap)}
                options={Object.keys(CalcType).map((type) => ({
                  value: type,
                  label: <CalcTypeTag type={type as CalcType} />,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <InstitutionSelect
              formItemProps={{
                name: "institutionId",
                label: t("calc.blacklist.attrs.institutionId"),
                rules: [validations.notNull],
              }}
              optionsProps={{
                includeDeactivated: true,
                filterType: InstitutionType.INSURANCE_COMPANY,
              }}
            />
          </Col>

          <Col span={8}>
            <Form.Item name="agentId" label={t("calc.blacklist.attrs.agentId")} rules={[validations.notNull]}>
              <AgentSelect />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
